// Leaderboard Admin Component
// Admin interface for creating and managing leaderboards for completed events

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  EyeOff,
  Trophy, 
  Users, 
  Award,
  Crown,
  Medal,
  Target,
  Save,
  X,
  AlertCircle,
  CheckCircle,
  TrendingUp,
  Calendar,
  Timer,
  Star,
  BarChart3,
  Copy,
  Download,
  Settings
} from 'lucide-react';
import { useRealtime } from '../../contexts/RealtimeContext';
import { 
  createLeaderboard,
  updateLeaderboard,
  deleteLeaderboard,
  getAllLeaderboards,
  listenToAllLeaderboards,
  publishLeaderboard,
  unpublishLeaderboard,
  getLeaderboardStats,
  addLeaderboardParticipant,
  updateParticipantScore,
  removeLeaderboardParticipant
} from '../../firebase/leaderboards';
import { getAllActivityRegistrations } from '../../firebase/activityRegistrations';
import { getActivityParticipants } from '../../firebase/activityRegistrations';
import toast from 'react-hot-toast';

const LeaderboardAdmin = () => {
  const { currentUser, logAdminAction } = useRealtime();
  const [leaderboards, setLeaderboards] = useState([]);
  const [activityRegistrations, setActivityRegistrations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('leaderboards');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingLeaderboard, setEditingLeaderboard] = useState(null);
  const [selectedLeaderboard, setSelectedLeaderboard] = useState(null);
  const [leaderboardStats, setLeaderboardStats] = useState(null);
  const [showParticipantModal, setShowParticipantModal] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [selectedLeaderboards, setSelectedLeaderboards] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    description: '',
    isPublished: false,
    participants: []
  });

  // Participant form state
  const [participantForm, setParticipantForm] = useState({
    name: '',
    email: '',
    rank: '',
    achievement: '',
    notes: ''
  });

  // Load leaderboards
  useEffect(() => {
    const unsubscribe = listenToAllLeaderboards((result) => {
      if (result.success) {
        setLeaderboards(result.data);
        setIsLoading(false);
      } else {
        setIsLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  // Load activity registrations
  useEffect(() => {
    const loadRegistrations = async () => {
      const result = await getAllActivityRegistrations();
      if (result.success) {
        setActivityRegistrations(result.data);
      }
    };
    loadRegistrations();
  }, []);

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle winner details changes
  const handleWinnerDetailsChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      winnerDetails: {
        ...prev.winnerDetails,
        [field]: value
      }
    }));
  };

  // Handle participant form changes
  const handleParticipantFormChange = (field, value) => {
    setParticipantForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Create leaderboard
  const handleCreateLeaderboard = async () => {
    if (!formData.description) {
      toast.error('Please enter a description for the leaderboard');
      return;
    }

    try {
      // Create leaderboard with participants
      const leaderboardData = {
        description: formData.description,
        isPublished: formData.isPublished,
        participants: formData.participants || [],
        totalParticipants: formData.participants?.length || 0
      };

      const result = await createLeaderboard(leaderboardData);
      if (result.success) {
        toast.success('Leaderboard created successfully!');
        setShowCreateForm(false);
        setFormData({
          description: '',
          isPublished: false,
          participants: []
        });
        
        // Log admin action
        if (logAdminAction) {
          await logAdminAction('leaderboard_created', `Created leaderboard: ${formData.description}`);
        }
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      toast.error('Failed to create leaderboard');
    }
  };

  // Update leaderboard
  const handleUpdateLeaderboard = async () => {
    if (!editingLeaderboard) return;

    try {
      // Update leaderboard with participants
      const leaderboardData = {
        description: formData.description,
        isPublished: formData.isPublished,
        participants: formData.participants || [],
        totalParticipants: formData.participants?.length || 0
      };

      const result = await updateLeaderboard(editingLeaderboard.id, leaderboardData);
      if (result.success) {
        toast.success('Leaderboard updated successfully!');
        setEditingLeaderboard(null);
        setFormData({
          description: '',
          isPublished: false,
          participants: []
        });
        
        // Log admin action
        if (logAdminAction) {
          await logAdminAction('leaderboard_updated', `Updated leaderboard: ${formData.description}`);
        }
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      toast.error('Failed to update leaderboard');
    }
  };

  // Show delete confirmation modal
  const showDeleteConfirmation = (leaderboardId, title) => {
    setDeleteTarget({ id: leaderboardId, title, type: 'single' });
    setShowDeleteModal(true);
  };

  // Delete leaderboard
  const handleDeleteLeaderboard = async () => {
    if (!deleteTarget) return;

    try {
      const result = await deleteLeaderboard(deleteTarget.id);
      if (result.success) {
        toast.success('Leaderboard deleted successfully!');
        
        // Log admin action
        if (logAdminAction) {
          await logAdminAction('leaderboard_deleted', `Deleted leaderboard: ${deleteTarget.title}`);
        }
        
        setShowDeleteModal(false);
        setDeleteTarget(null);
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      toast.error('Failed to delete leaderboard');
    }
  };

  // Delete participant from leaderboard
  const handleDeleteParticipant = async (leaderboardId, participantId, participantName) => {
    if (!window.confirm(`Are you sure you want to remove ${participantName} from this leaderboard?`)) return;

    try {
      const result = await removeLeaderboardParticipant(leaderboardId, participantId);
      if (result.success) {
        toast.success('Participant removed successfully!');
        
        // Log admin action
        if (logAdminAction) {
          await logAdminAction('participant_removed', `Removed participant: ${participantName}`);
        }
        
        // Refresh leaderboard data
        if (selectedLeaderboard) {
          handleViewLeaderboard(selectedLeaderboard);
        }
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      toast.error('Failed to remove participant');
    }
  };

  // Toggle leaderboard selection for bulk actions
  const toggleLeaderboardSelection = (leaderboardId) => {
    setSelectedLeaderboards(prev => 
      prev.includes(leaderboardId) 
        ? prev.filter(id => id !== leaderboardId)
        : [...prev, leaderboardId]
    );
  };

  // Select all leaderboards
  const selectAllLeaderboards = () => {
    setSelectedLeaderboards(leaderboards.map(lb => lb.id));
  };

  // Clear all selections
  const clearAllSelections = () => {
    setSelectedLeaderboards([]);
  };

  // Show bulk delete confirmation
  const showBulkDeleteConfirmation = () => {
    if (selectedLeaderboards.length === 0) {
      toast.error('Please select leaderboards to delete');
      return;
    }
    
    setDeleteTarget({ 
      ids: selectedLeaderboards, 
      count: selectedLeaderboards.length, 
      type: 'bulk' 
    });
    setShowDeleteModal(true);
  };

  // Bulk delete leaderboards
  const handleBulkDelete = async () => {
    if (!deleteTarget || deleteTarget.type !== 'bulk') return;

    try {
      const deletePromises = deleteTarget.ids.map(leaderboardId => 
        deleteLeaderboard(leaderboardId)
      );
      
      const results = await Promise.all(deletePromises);
      const successCount = results.filter(result => result.success).length;
      
      if (successCount === deleteTarget.ids.length) {
        toast.success(`${successCount} leaderboard(s) deleted successfully!`);
        
        // Log admin action
        if (logAdminAction) {
          await logAdminAction('bulk_delete_leaderboards', `Deleted ${successCount} leaderboards`);
        }
        
        setSelectedLeaderboards([]);
        setShowBulkActions(false);
      } else {
        toast.error(`Only ${successCount} out of ${deleteTarget.ids.length} leaderboards were deleted`);
      }
      
      setShowDeleteModal(false);
      setDeleteTarget(null);
    } catch (error) {
      toast.error('Failed to delete leaderboards');
    }
  };

  // Publish/Unpublish leaderboard
  const handleTogglePublish = async (leaderboardId, isPublished, title) => {
    try {
      const result = isPublished 
        ? await unpublishLeaderboard(leaderboardId)
        : await publishLeaderboard(leaderboardId);
        
      if (result.success) {
        toast.success(`Leaderboard ${isPublished ? 'unpublished' : 'published'} successfully!`);
        
        // Log admin action
        if (logAdminAction) {
          await logAdminAction(
            isPublished ? 'leaderboard_unpublished' : 'leaderboard_published', 
            `${isPublished ? 'Unpublished' : 'Published'} leaderboard: ${title}`
          );
        }
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      toast.error(`Failed to ${isPublished ? 'unpublish' : 'publish'} leaderboard`);
    }
  };

  // Edit leaderboard
  const handleEditLeaderboard = (leaderboard) => {
    setEditingLeaderboard(leaderboard);
    setFormData({
      description: leaderboard.description || '',
      isPublished: leaderboard.isPublished || false,
      participants: leaderboard.participants || []
    });
    setShowCreateForm(true);
  };

  // View leaderboard details
  const handleViewLeaderboard = async (leaderboard) => {
    setSelectedLeaderboard(leaderboard);
    
    // Load participants
    if (leaderboard.registrationId) {
      const participantsResult = await getActivityParticipants(leaderboard.registrationId);
      if (participantsResult.success) {
        setParticipants(participantsResult.data);
      }
    }
    
    // Load stats
    const statsResult = await getLeaderboardStats(leaderboard.id);
    if (statsResult.success) {
      setLeaderboardStats(statsResult.data);
    }
  };

  // Add participant to form data
  const handleAddParticipant = () => {
    if (!participantForm.name || !participantForm.rank) {
      toast.error('Please fill in participant name and rank');
      return;
    }

    const newParticipant = {
      name: participantForm.name,
      email: participantForm.email,
      rank: parseInt(participantForm.rank),
      achievement: participantForm.achievement,
      notes: participantForm.notes,
      userId: `manual_${Date.now()}` // Generate unique ID for manual entries
    };

    setFormData(prev => ({
      ...prev,
      participants: [...prev.participants, newParticipant]
    }));

    setParticipantForm({
      name: '',
      email: '',
      rank: '',
      achievement: '',
      notes: ''
    });
    setShowParticipantModal(false);
    toast.success('Participant added to form!');
  };

  // Get activity name by ID
  const getActivityName = (registrationId) => {
    const activity = activityRegistrations.find(reg => reg.id === registrationId);
    return activity ? activity.title : 'Unknown Activity';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading leaderboards...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center">
                <Trophy className="h-8 w-8 mr-3 text-yellow-500" />
                Leaderboard Management
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-2">
                Create and manage leaderboards for completed events
              </p>
            </div>
            <button
              onClick={() => setShowCreateForm(true)}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="h-5 w-5 mr-2" />
              Create Leaderboard
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('leaderboards')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'leaderboards'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <Trophy className="h-4 w-4 inline mr-2" />
                Leaderboards ({leaderboards.length})
              </button>
            </nav>
          </div>
        </div>

        {/* Bulk Actions */}
        {leaderboards.length > 0 && (
          <div className="mb-6 flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setShowBulkActions(!showBulkActions)}
                className="flex items-center px-3 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                <Settings className="h-4 w-4 mr-2" />
                Bulk Actions
              </button>
              
              {showBulkActions && (
                <div className="flex items-center space-x-2">
                  <button
                    onClick={selectAllLeaderboards}
                    className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    Select All
                  </button>
                  <span className="text-gray-400">|</span>
                  <button
                    onClick={clearAllSelections}
                    className="text-sm text-gray-600 dark:text-gray-400 hover:underline"
                  >
                    Clear All
                  </button>
                  {selectedLeaderboards.length > 0 && (
                    <>
                      <span className="text-gray-400">|</span>
                      <button
                        onClick={showBulkDeleteConfirmation}
                        className="text-sm text-red-600 dark:text-red-400 hover:underline"
                      >
                        Delete Selected ({selectedLeaderboards.length})
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
            
            {selectedLeaderboards.length > 0 && (
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {selectedLeaderboards.length} leaderboard(s) selected
              </div>
            )}
          </div>
        )}

        {/* Leaderboards List */}
        {activeTab === 'leaderboards' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {leaderboards.map((leaderboard, index) => (
              <motion.div
                key={leaderboard.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`bg-white dark:bg-gray-800 rounded-xl shadow-lg border overflow-hidden transition-all ${
                  selectedLeaderboards.includes(leaderboard.id)
                    ? 'border-blue-500 ring-2 ring-blue-200 dark:ring-blue-800'
                    : 'border-gray-200 dark:border-gray-700'
                }`}
              >
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      {showBulkActions && (
                        <input
                          type="checkbox"
                          checked={selectedLeaderboards.includes(leaderboard.id)}
                          onChange={() => toggleLeaderboardSelection(leaderboard.id)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-3"
                        />
                      )}
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {leaderboard.description || 'Leaderboard'}
                      </h3>
                    </div>
                    <div className="flex items-center space-x-2">
                      {leaderboard.isPublished ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                          <Eye className="h-3 w-3 mr-1" />
                          Published
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                          <EyeOff className="h-3 w-3 mr-1" />
                          Draft
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                      <Users className="h-4 w-4 mr-2" />
                      <span>{leaderboard.totalParticipants || 0} participants</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                      <Trophy className="h-4 w-4 mr-2" />
                      <span>Rank-based leaderboard</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleViewLeaderboard(leaderboard)}
                        className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 dark:text-gray-400 dark:hover:text-blue-400 dark:hover:bg-blue-900 rounded-lg transition-colors"
                        title="View Details"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleEditLeaderboard(leaderboard)}
                        className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 dark:text-gray-400 dark:hover:text-green-400 dark:hover:bg-green-900 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleTogglePublish(leaderboard.id, leaderboard.isPublished, leaderboard.title)}
                        className={`p-2 rounded-lg transition-colors ${
                          leaderboard.isPublished
                            ? 'text-gray-600 hover:text-red-600 hover:bg-red-50 dark:text-gray-400 dark:hover:text-red-400 dark:hover:bg-red-900'
                            : 'text-gray-600 hover:text-green-600 hover:bg-green-50 dark:text-gray-400 dark:hover:text-green-400 dark:hover:bg-green-900'
                        }`}
                        title={leaderboard.isPublished ? 'Unpublish' : 'Publish'}
                      >
                        {leaderboard.isPublished ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                      <button
                        onClick={() => showDeleteConfirmation(leaderboard.id, leaderboard.title)}
                        className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 dark:text-gray-400 dark:hover:text-red-400 dark:hover:bg-red-900 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-500">
                      {new Date(leaderboard.createdAt?.toDate?.() || leaderboard.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Create/Edit Form Modal */}
        <AnimatePresence>
          {showCreateForm && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              >
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                      {editingLeaderboard ? 'Edit Leaderboard' : 'Create New Leaderboard'}
                    </h2>
                    <button
                      onClick={() => {
                        setShowCreateForm(false);
                        setEditingLeaderboard(null);
                        setFormData({
                          title: '',
                          description: '',
                          registrationId: '',
                          eventName: '',
                          eventDate: '',
                          leaderboardType: 'complete',
                          maxParticipants: 50,
                          isPublished: false,
                          additionalInfo: '',
                          winnerDetails: {
                            name: '',
                            score: '',
                            achievement: '',
                            prize: ''
                          }
                        });
                      }}
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      <X className="h-6 w-6" />
                    </button>
                  </div>

                  <div className="space-y-6">
                    {/* Description */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Leaderboard Description *
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => handleInputChange('description', e.target.value)}
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        placeholder="Enter a description for this leaderboard (e.g., 'Math Competition Results', 'Sports Day Winners', etc.)"
                      />
                    </div>

                    {/* Participants Section */}
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                          Participants
                        </h3>
                        <button
                          type="button"
                          onClick={() => setShowParticipantModal(true)}
                          className="flex items-center px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add Participant
                        </button>
                      </div>

                      {/* Participants List */}
                      {formData.participants && formData.participants.length > 0 ? (
                        <div className="space-y-2 max-h-60 overflow-y-auto">
                          {formData.participants
                            .sort((a, b) => a.rank - b.rank)
                            .map((participant, index) => {
                              const getRankColor = (rank) => {
                                if (rank === 1) return 'bg-gradient-to-r from-yellow-400 to-yellow-600 text-white';
                                if (rank === 2) return 'bg-gradient-to-r from-gray-300 to-gray-500 text-white';
                                if (rank === 3) return 'bg-gradient-to-r from-orange-400 to-orange-600 text-white';
                                return 'bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200';
                              };

                              const getRankIcon = (rank) => {
                                if (rank === 1) return <Crown className="h-4 w-4" />;
                                if (rank === 2) return <Medal className="h-4 w-4" />;
                                if (rank === 3) return <Award className="h-4 w-4" />;
                                return <span className="font-bold text-sm">{rank}</span>;
                              };

                              return (
                                <div
                                  key={index}
                                  className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
                                >
                                  <div className="flex items-center">
                                    <div className={`flex items-center justify-center w-8 h-8 rounded-full mr-3 ${getRankColor(participant.rank)}`}>
                                      {getRankIcon(participant.rank)}
                                    </div>
                                    <div>
                                      <p className="font-medium text-gray-900 dark:text-white">
                                        {participant.name}
                                      </p>
                                      {participant.achievement && (
                                        <p className="text-sm text-blue-600 dark:text-blue-400">
                                          {participant.achievement}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <span className="text-sm font-bold text-gray-900 dark:text-white">
                                      Rank #{participant.rank}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const updatedParticipants = formData.participants.filter((_, i) => i !== index);
                                        handleInputChange('participants', updatedParticipants);
                                      }}
                                      className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900 rounded transition-colors"
                                      title="Remove Participant"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                          <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                          <p>No participants added yet</p>
                          <p className="text-sm">Click "Add Participant" to get started</p>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="isPublished"
                        checked={formData.isPublished}
                        onChange={(e) => handleInputChange('isPublished', e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="isPublished" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                        Publish immediately
                      </label>
                    </div>
                  </div>

                  <div className="flex justify-end space-x-3 mt-6">
                    <button
                      onClick={() => {
                        setShowCreateForm(false);
                        setEditingLeaderboard(null);
                      }}
                      className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={editingLeaderboard ? handleUpdateLeaderboard : handleCreateLeaderboard}
                      className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Save className="h-4 w-4 mr-2" />
                      {editingLeaderboard ? 'Update' : 'Create'} Leaderboard
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Leaderboard Details Modal */}
        <AnimatePresence>
          {selectedLeaderboard && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              >
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                      {selectedLeaderboard.title}
                    </h2>
                    <button
                      onClick={() => setSelectedLeaderboard(null)}
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      <X className="h-6 w-6" />
                    </button>
                  </div>

                  {/* Leaderboard Stats */}
                  {leaderboardStats && (
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                      <div className="bg-blue-50 dark:bg-blue-900 p-4 rounded-lg">
                        <div className="flex items-center">
                          <Users className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                          <div className="ml-3">
                            <p className="text-sm font-medium text-blue-600 dark:text-blue-400">Total Participants</p>
                            <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">
                              {leaderboardStats.totalParticipants}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="bg-green-50 dark:bg-green-900 p-4 rounded-lg">
                        <div className="flex items-center">
                          <TrendingUp className="h-8 w-8 text-green-600 dark:text-green-400" />
                          <div className="ml-3">
                            <p className="text-sm font-medium text-green-600 dark:text-green-400">Average Score</p>
                            <p className="text-2xl font-bold text-green-900 dark:text-green-100">
                              {leaderboardStats.averageScore.toFixed(1)}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="bg-yellow-50 dark:bg-yellow-900 p-4 rounded-lg">
                        <div className="flex items-center">
                          <Crown className="h-8 w-8 text-yellow-600 dark:text-yellow-400" />
                          <div className="ml-3">
                            <p className="text-sm font-medium text-yellow-600 dark:text-yellow-400">Highest Score</p>
                            <p className="text-2xl font-bold text-yellow-900 dark:text-yellow-100">
                              {leaderboardStats.highestScore}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="bg-purple-50 dark:bg-purple-900 p-4 rounded-lg">
                        <div className="flex items-center">
                          <BarChart3 className="h-8 w-8 text-purple-600 dark:text-purple-400" />
                          <div className="ml-3">
                            <p className="text-sm font-medium text-purple-600 dark:text-purple-400">Status</p>
                            <p className="text-lg font-bold text-purple-900 dark:text-purple-100">
                              {leaderboardStats.isPublished ? 'Published' : 'Draft'}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Participants List */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                        Participants ({selectedLeaderboard.participants?.length || 0})
                      </h3>
                      <button
                        onClick={() => setShowParticipantModal(true)}
                        className="flex items-center px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Participant
                      </button>
                    </div>

                    {selectedLeaderboard.participants && selectedLeaderboard.participants.length > 0 ? (
                      <div className="space-y-2">
                        {selectedLeaderboard.participants
                          .sort((a, b) => a.rank - b.rank)
                          .map((participant, index) => {
                            // Color coding for top 3 ranks
                            const getRankColor = (rank) => {
                              if (rank === 1) return 'bg-gradient-to-r from-yellow-400 to-yellow-600 text-white';
                              if (rank === 2) return 'bg-gradient-to-r from-gray-300 to-gray-500 text-white';
                              if (rank === 3) return 'bg-gradient-to-r from-orange-400 to-orange-600 text-white';
                              return 'bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200';
                            };

                            const getRankIcon = (rank) => {
                              if (rank === 1) return <Crown className="h-4 w-4" />;
                              if (rank === 2) return <Medal className="h-4 w-4" />;
                              if (rank === 3) return <Award className="h-4 w-4" />;
                              return <span className="font-bold">{rank}</span>;
                            };

                            return (
                              <div
                                key={participant.userId || index}
                                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg"
                              >
                                <div className="flex items-center">
                                  <div className={`flex items-center justify-center w-10 h-10 rounded-full font-bold mr-3 ${getRankColor(participant.rank)}`}>
                                    {getRankIcon(participant.rank)}
                                  </div>
                                  <div>
                                    <p className="font-medium text-gray-900 dark:text-white">
                                      {participant.name}
                                    </p>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                      {participant.email}
                                    </p>
                                    {participant.achievement && (
                                      <p className="text-sm text-blue-600 dark:text-blue-400 font-medium">
                                        {participant.achievement}
                                      </p>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center space-x-4">
                                  <div className="text-right">
                                    <p className="font-bold text-lg text-gray-900 dark:text-white">
                                      Rank #{participant.rank}
                                    </p>
                                    {participant.notes && (
                                      <p className="text-sm text-gray-600 dark:text-gray-400">
                                        {participant.notes}
                                      </p>
                                    )}
                                  </div>
                                  <button
                                    onClick={() => handleDeleteParticipant(selectedLeaderboard.id, participant.userId, participant.name)}
                                    className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900 rounded-lg transition-colors"
                                    title="Remove Participant"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                        <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p>No participants added yet</p>
                      </div>
                    )}
                  </div>

                  {/* Additional Information */}
                  {selectedLeaderboard.additionalInfo && (
                    <div className="mb-6">
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                        Additional Information
                      </h3>
                      <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                          {selectedLeaderboard.additionalInfo}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Add Participant Modal */}
        <AnimatePresence>
          {showParticipantModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full"
              >
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                      Add Participant
                    </h2>
                    <button
                      onClick={() => setShowParticipantModal(false)}
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      <X className="h-6 w-6" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Name *
                      </label>
                      <input
                        type="text"
                        value={participantForm.name}
                        onChange={(e) => handleParticipantFormChange('name', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        placeholder="Enter participant name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        value={participantForm.email}
                        onChange={(e) => handleParticipantFormChange('email', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        placeholder="Enter participant email"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Rank *
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={participantForm.rank}
                        onChange={(e) => handleParticipantFormChange('rank', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        placeholder="Enter rank (1, 2, 3, etc.)"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Multiple participants can have the same rank
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Achievement
                      </label>
                      <input
                        type="text"
                        value={participantForm.achievement}
                        onChange={(e) => handleParticipantFormChange('achievement', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        placeholder="Enter achievement (e.g., 'Best Performer', 'Fastest Runner')"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Notes
                      </label>
                      <textarea
                        value={participantForm.notes}
                        onChange={(e) => handleParticipantFormChange('notes', e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        placeholder="Enter any additional notes"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end space-x-3 mt-6">
                    <button
                      onClick={() => setShowParticipantModal(false)}
                      className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleAddParticipant}
                      className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Participant
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Delete Confirmation Modal */}
        <AnimatePresence>
          {showDeleteModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full"
              >
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
                      <AlertCircle className="h-6 w-6 mr-2 text-red-500" />
                      Confirm Deletion
                    </h2>
                    <button
                      onClick={() => {
                        setShowDeleteModal(false);
                        setDeleteTarget(null);
                      }}
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      <X className="h-6 w-6" />
                    </button>
                  </div>

                  <div className="mb-6">
                    {deleteTarget?.type === 'single' ? (
                      <div>
                        <p className="text-gray-700 dark:text-gray-300 mb-4">
                          Are you sure you want to delete the leaderboard <strong>"{deleteTarget.title}"</strong>?
                        </p>
                        <div className="bg-red-50 dark:bg-red-900 p-4 rounded-lg">
                          <p className="text-sm text-red-800 dark:text-red-200">
                            <strong>Warning:</strong> This action cannot be undone. All participant data and leaderboard information will be permanently deleted.
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <p className="text-gray-700 dark:text-gray-300 mb-4">
                          Are you sure you want to delete <strong>{deleteTarget?.count} leaderboard(s)</strong>?
                        </p>
                        <div className="bg-red-50 dark:bg-red-900 p-4 rounded-lg">
                          <p className="text-sm text-red-800 dark:text-red-200">
                            <strong>Warning:</strong> This action cannot be undone. All selected leaderboards and their data will be permanently deleted.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={() => {
                        setShowDeleteModal(false);
                        setDeleteTarget(null);
                      }}
                      className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={deleteTarget?.type === 'single' ? handleDeleteLeaderboard : handleBulkDelete}
                      className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete {deleteTarget?.type === 'single' ? 'Leaderboard' : 'Leaderboards'}
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default LeaderboardAdmin;
